import React, { useState } from "react";
import { Button, DatePicker, Select, Space } from "antd";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

export type DatePreset =
  | "all"
  | "today"
  | "yesterday"
  | "this_week"
  | "this_month"
  | "this_year"
  | "custom";

const PRESET_OPTIONS = [
  { value: "all", label: "All" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom" },
];

function rangeForPreset(preset: DatePreset): [dayjs.Dayjs, dayjs.Dayjs] | null {
  switch (preset) {
    case "today":
      return [dayjs().startOf("day"), dayjs().endOf("day")];
    case "yesterday":
      return [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")];
    case "this_week":
      return [dayjs().startOf("week"), dayjs().endOf("week")];
    case "this_month":
      return [dayjs().startOf("month"), dayjs().endOf("month")];
    case "this_year":
      return [dayjs().startOf("year"), dayjs().endOf("year")];
    default:
      return null;
  }
}

interface Props {
  value: [dayjs.Dayjs, dayjs.Dayjs] | null;
  defaultPreset?: DatePreset;
  onChange: (range: [dayjs.Dayjs, dayjs.Dayjs] | null) => void;
  onApply: (range: [dayjs.Dayjs, dayjs.Dayjs] | null) => void;
}

const DatePresetFilter: React.FC<Props> = ({
  value,
  defaultPreset = "this_week",
  onChange,
  onApply,
}) => {
  const [preset, setPreset] = useState<DatePreset>(defaultPreset);
  const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(value);

  const handlePresetChange = (p: DatePreset) => {
    setPreset(p);
    if (p === "custom") return;
    const range = rangeForPreset(p);
    onChange(range);
    onApply(range);
  };

  return (
    <Space size={6}>
      <Select
        value={preset}
        onChange={handlePresetChange}
        options={PRESET_OPTIONS}
        style={{ width: 130 }}
        size="small"
      />
      {preset === "custom" && (
        <>
          <RangePicker
            size="small"
            value={customRange}
            allowClear={false}
            onChange={(dates) => {
              if (dates) setCustomRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
            }}
          />
          <Button
            size="small"
            type="primary"
            onClick={() => {
              onChange(customRange);
              onApply(customRange);
            }}
          >
            Apply
          </Button>
        </>
      )}
    </Space>
  );
};

export default DatePresetFilter;
